<template>
  <div class="project-map" v-click-outside="closePopup">
    <MapComponent
      ref="map"
      :geojson="currentGeojson"
      :mapConfig="currentMapConfig"
      :styleFeature="styleFeature"
      :featureTooltip="featureTooltip"
      :isFeatureSelected="isFeatureSelected"
      height="100%"
      :popupOpen="popupState.isOpen"
      @featureClick="onFeatureClick"
      @ready="onMapReady"
      @closePopup="closePopup"
    >
      <template #popupHeader>
        <ProjectPopupHeader
          :state="popupState"
          @countryClick="onCountryClick"
          @specialTerritoryClick="onSpecialTerritoryClick"
        />
      </template>
      <template #popupBody>
        <ProjectPopupBody :state="popupState" @regionClick="onRegionClick" />
      </template>
    </MapComponent>
  </div>
</template>

<script>
  import L from 'leaflet';

  const _origAnimateZoom = L.Tooltip.prototype._animateZoom;
  L.Tooltip.prototype._animateZoom = function (e) {
    if (!this._map) return;
    _origAnimateZoom.call(this, e);
  };

  const _origUpdatePosition = L.Tooltip.prototype._updatePosition;
  L.Tooltip.prototype._updatePosition = function () {
    if (!this._map) return;
    _origUpdatePosition.call(this);
  };

  import MapComponent from '@/common/components/MapComponent/MapComponent.vue';
  import worldMap from '@/common/custom.geo.json';
  import ChineseRegions from '@/common/custom.regions.china.json';
  import RussianRegions from '@/common/region.json';
  import API_COUNTRY from '@/common/utils';

  import {
    CHINA_CODE,
    COUNTRY_NAME,
    HONG_KONG_CODE,
    MACAU_CODE,
    MAP_CONFIG,
    REGION_TO_COUNTRY_MAPPING,
    RUSSIA_CODE,
    SPECIAL_TERRITORY_MAPPING,
    TAIWAN_CODE,
  } from './constants';
  import * as helpers from './helpers';
  import ProjectPopupBody from './popup/ProjectPopupBody.vue';
  import ProjectPopupHeader from './popup/ProjectPopupHeader.vue';

  export default {
    name: 'MpkProjectMap',
    components: { MapComponent, ProjectPopupHeader, ProjectPopupBody },
    props: {
      projects: {
        type: Object,
        default: () => ({}),
      },
      maxProjects: {
        type: Number,
        default: 0,
      },
      selectedCountryId: {
        type: Number,
        default: null,
      },
    },
    emits: ['onCountryHeaderClick', 'onRegionClick', 'maxRegionProjectsChanged'],
    data() {
      return {
        countries: [],
        selectedWorldCountryCode: null,
        selectedRegionType: null,
        selectedRegionCodes: [],
        isRegionsMap: false,
        currentRegionType: null,
        isSwitchingMap: false,
        isUnmounted: false,
        currentGeojson: worldMap,
        currentMapConfig: MAP_CONFIG.WORLD,
        pendingSpecialRegion: null,
        popupState: {
          countryName: '',
          countProjects: 0,
          countMpkProjects: 0,
          investmentsSum: null,
          id: null,
          projects: [],
          regionProjects: [],
          isSpecialTerritory: false,
          specialTerritoryCountryId: null,
          isOpen: false,
        },
      };
    },
    computed: {
      maxRegionProjects() {
        if (!this.isRegionsMap) return this.maxProjects;

        const isRussia = this.currentRegionType === COUNTRY_NAME.RUSSIA;

        if (!isRussia) {
          const selectedCountry = this.countries.find((c) => c.id === this.selectedCountryId);
          if (selectedCountry && helpers.isSpecialTerritoryCode(selectedCountry.code)) {
            return this.projects[selectedCountry.code]?.mpkProjects?.length ?? 0;
          }
        }

        const countryCode = isRussia ? RUSSIA_CODE : CHINA_CODE;
        return helpers.getMaxRegionProjects(this.projects, countryCode, isRussia);
      },
    },
    mounted() {
      this.loadCountries();
    },
    beforeUnmount() {
      this.isUnmounted = true;
    },
    methods: {
      restyle() {
        this.$refs.map?.restyle();
      },

      async loadCountries() {
        const response = await API_COUNTRY.searchNsi('countryListWithData', true);
        if (this.isUnmounted) return;
        this.countries = response.data;
        this.checkAndSwitchMap();

        if (!this.isRegionsMap) this.$refs.map?.rebuild();
      },

      checkAndSwitchMap() {
        if (this.isSwitchingMap) return;
        this.isSwitchingMap = true;

        try {
          if (!this.selectedCountryId) {
            this.selectedWorldCountryCode = null;
            this.resetSelectedRegionCodes();
            this.setupWorldMap();
            return;
          }

          const countryCode = helpers.getCountryCode(this.selectedCountryId, this.countries);

          if (countryCode === RUSSIA_CODE) {
            if (this.selectedRegionType !== COUNTRY_NAME.RUSSIA) {
              this.resetSelectedRegionCodes();
            }
            this.setupRegionalMap(COUNTRY_NAME.RUSSIA);
          } else if ([CHINA_CODE, TAIWAN_CODE, HONG_KONG_CODE, MACAU_CODE].includes(countryCode)) {
            if (this.selectedRegionType !== COUNTRY_NAME.CHINA) {
              this.resetSelectedRegionCodes();
            }
            this.setupRegionalMap(COUNTRY_NAME.CHINA, countryCode);

            if (helpers.isSpecialTerritoryCode(countryCode)) {
              const regionCode = SPECIAL_TERRITORY_MAPPING[countryCode];
              const region = ChineseRegions.features.find((f) => f.properties.code === regionCode);
              this.pendingSpecialRegion = { regionCode, regionName: region?.properties?.nameRu || '' };
            }
          } else {
            this.selectedWorldCountryCode = countryCode ?? null;
            this.resetSelectedRegionCodes();
            this.setupWorldMap();
          }
        } finally {
          this.isSwitchingMap = false;
        }
      },

      setupWorldMap() {
        this.isRegionsMap = false;
        this.currentRegionType = null;
        this.currentMapConfig = MAP_CONFIG.WORLD;
        this.currentGeojson = worldMap;
      },

      setupRegionalMap(regionType, countryCode = null) {
        this.isRegionsMap = true;
        this.currentRegionType = regionType;
        this.currentMapConfig = helpers.getRegionalMapConfig(regionType, countryCode);
        this.currentGeojson = regionType === COUNTRY_NAME.RUSSIA ? RussianRegions : ChineseRegions;
      },

      onMapReady() {
        if (!this.pendingSpecialRegion) return;

        const { regionCode, regionName } = this.pendingSpecialRegion;
        this.pendingSpecialRegion = null;
        this.handleRegionClick(regionCode, regionName);
      },

      styleFeature(feature) {
        return this.isRegionsMap ? this.styleRegionMap(feature) : this.styleWorldMap(feature);
      },

      styleWorldMap(feature) {
        return helpers.styleWorldMap(feature, this.projects, this.maxProjects);
      },

      styleRegionMap(feature) {
        return helpers.styleRegionMap(
          feature,
          this.projects,
          this.countries,
          this.maxRegionProjects,
          this.currentRegionType,
          this.selectedCountryId,
        );
      },

      featureTooltip(feature) {
        if (this.isRegionsMap) return feature?.properties?.nameRu ?? '';

        const code = Number(feature?.properties?.iso_n3);
        const country = helpers.findCountryByCode(code, this.countries);
        if (country) feature.properties.rusName = country.name;

        if (!feature?.properties?.rusName) return '';

        return helpers.getCountryName(
          this.countries.find((c) => c.code === code),
          feature,
        );
      },

      isFeatureSelected(feature) {
        return this.isRegionsMap
          ? this.isSelectedRegionCode(feature?.properties?.code)
          : this.isSelectedWorldFeature(feature);
      },

      isSelectedWorldFeature(feature) {
        if (!this.selectedWorldCountryCode) return false;
        if (!feature?.properties?.iso_n3) return false;

        return Number(feature.properties.iso_n3) === this.selectedWorldCountryCode;
      },

      isSelectedRegionCode(regionCode) {
        if (this.selectedRegionType !== this.currentRegionType) return false;
        if (![COUNTRY_NAME.RUSSIA, COUNTRY_NAME.CHINA].includes(this.currentRegionType)) return false;

        return this.selectedRegionCodes.includes(String(regionCode));
      },

      onFeatureClick(feature) {
        if (this.isRegionsMap) {
          this.handleRegionClick(feature?.properties?.code, feature?.properties?.nameRu);
          return;
        }
        this.handleWorldClick(feature);
      },

      handleWorldClick(feature) {
        const code = Number(feature.properties.iso_n3);
        const country = helpers.findCountryByCode(code, this.countries);
        if (!country) return;

        this.selectedWorldCountryCode = code;
        this.restyle();

        const currentProject = this.projects[code];
        const countryData = this.countries.find((c) => c.code === code);
        const countryName = helpers.getCountryName(countryData, feature);

        this.setPopupData({
          countryName,
          countMpkProjects: currentProject?.countProjectsHasMpk ?? 0,
          projects: currentProject?.mpkProjects ?? [],
          countProjects: currentProject?.mpkProjects?.length ?? 0,
          id: country.id,
          mpkProjects: currentProject?.mpkProjects ?? [],
          regionProjects: [
            ...(currentProject?.projectsByRegionsRussia ?? []),
            ...(currentProject?.projectsByRegionsChina ?? []),
          ],
        });

        this.openPopup();
      },

      handleRegionClick(regionCode, regionName) {
        this.applySelectedRegionCodesByFeatureCode(regionCode);
        this.restyle();

        if (
          helpers.isSpecialTerritoryRegion(regionCode, this.selectedCountryId, this.currentRegionType, this.countries)
        ) {
          this.handleSpecialTerritoryClick(regionCode, regionName);
          return;
        }

        const specialData = helpers.getSpecialTerritoryData(
          regionCode,
          this.selectedCountryId,
          this.currentRegionType,
          this.countries,
          this.projects,
        );

        if (specialData) {
          this.setPopupData({
            countryName: regionName,
            countProjects: specialData.countProjects,
            countMpkProjects: specialData.countProjectsHasMpk,
            investmentsSum: specialData.investmentsSum,
            projects: specialData.mpkProjects,
            mpkProjects: specialData.mpkProjects,
            id: this.selectedCountryId,
          });
          this.openPopup();
          return;
        }

        this.handleNormalRegionClick(regionCode, regionName);
      },

      handleSpecialTerritoryClick(regionCode, regionName) {
        const countryCode = REGION_TO_COUNTRY_MAPPING[regionCode];
        const specialCountry = helpers.findCountryByCode(countryCode, this.countries);

        if (specialCountry) {
          this.setPopupData({
            countryName: regionName,
            isSpecialTerritory: true,
            specialTerritoryCountryId: specialCountry.id,
            countProjects: 0,
            countMpkProjects: 0,
            id: specialCountry.id,
            projects: [],
            mpkProjects: specialCountry.mpkProjects,
            regionProjects: [],
          });
          this.openPopup();
        }
      },

      handleNormalRegionClick(regionCode, regionName) {
        const isRussia = this.currentRegionType === COUNTRY_NAME.RUSSIA;
        const countryCode = isRussia ? RUSSIA_CODE : CHINA_CODE;
        const countryData = this.projects[countryCode];

        const projectsByRegions = helpers.getProjectsByRegions(countryData, isRussia);
        const regionData = helpers.findRegionData(projectsByRegions, regionCode, isRussia);

        this.setPopupData({
          countryName: regionName,
          countProjects: regionData?.countProjects ?? 0,
          countMpkProjects: regionData?.countProjectsHasMpk ?? 0,
          projects: regionData?.mpkProjects ?? [],
          mpkProjects: regionData?.mpkProjects ?? [],
          code: countryCode,
          id: isRussia ? regionData?.regionOfRussia?.id : regionData?.regionOfChina?.id,
        });

        this.openPopup();
      },

      setPopupData(data) {
        Object.assign(this.popupState, {
          countryName: data.countryName || '',
          countProjects: data.countProjects || 0,
          countMpkProjects: data.countMpkProjects || 0,
          investmentsSum: this.getInvestmentsSumByProjects(data?.mpkProjects ?? []),
          id: data.id || null,
          projects: data.projects || [],
          regionProjects: data.regionProjects || [],
          isSpecialTerritory: data.isSpecialTerritory || false,
          specialTerritoryCountryId: data.specialTerritoryCountryId || null,
        });
      },

      getInvestmentsSumByProjects(mpkProjects) {
        return mpkProjects.reduce((sum, mpkProject) => {
          return sum + ((mpkProject.fromEhd ? mpkProject.investmentsAmount : mpkProject.totalInvestmentMlrdRub) ?? 0);
        }, 0);
      },

      openPopup() {
        this.popupState.isOpen = true;
      },

      closePopup() {
        this.popupState.isOpen = false;
      },

      onCountryClick(countryId) {
        const actualCountryId = this.isRegionsMap ? this.selectedCountryId : countryId;
        this.$emit('onCountryHeaderClick', actualCountryId);
      },

      onSpecialTerritoryClick() {
        if (!this.popupState.specialTerritoryCountryId) return;

        this.$emit('onRegionClick', {
          countryId: this.popupState.specialTerritoryCountryId,
          regionData: null,
        });
        this.closePopup();
      },

      onRegionClick(regionData) {
        if (this.isRegionsMap) {
          this.openRegionModalFromParent(regionData);
          return;
        }

        const countryCode = helpers.isRussianRegion(regionData) ? RUSSIA_CODE : CHINA_CODE;
        const country = helpers.findCountryByCode(countryCode, this.countries);

        this.closePopup();
        this.$emit('onRegionClick', {
          countryId: country.id,
          regionData,
        });
      },

      openRegionModalFromParent(regionData) {
        const regionName = helpers.getRegionName(regionData);
        const regionId = regionData.regionOfRussia?.id || regionData.regionOfChina?.id;

        this.selectedRegionType = helpers.getRegionTypeFromRegionData(regionData);
        this.selectedRegionCodes = helpers.getSelectedRegionCodesFromRegionData(regionData);
        this.restyle();

        this.setPopupData({
          countryName: regionName,
          countProjects: regionData.countProjects ?? 0,
          countMpkProjects: regionData.countProjectsHasMpk ?? 0,
          id: regionId,
          mpkProjects: regionData.mpkProjects,
          projects: regionData.mpkProjects ?? [],
        });

        this.openPopup();
      },

      resetSelectedRegionCodes() {
        this.selectedRegionType = null;
        this.selectedRegionCodes = [];
      },

      applySelectedRegionCodesByFeatureCode(regionCode) {
        if (![COUNTRY_NAME.RUSSIA, COUNTRY_NAME.CHINA].includes(this.currentRegionType)) return;

        const isRussia = this.currentRegionType === COUNTRY_NAME.RUSSIA;
        this.selectedRegionType = this.currentRegionType;

        const countryCode = isRussia ? RUSSIA_CODE : CHINA_CODE;
        const countryData = this.projects[countryCode];
        const projectsByRegions = helpers.getProjectsByRegions(countryData, isRussia);
        const regionData = helpers.findRegionData(projectsByRegions, regionCode, isRussia);

        this.selectedRegionCodes = regionData
          ? helpers.getSelectedRegionCodesFromRegionData(regionData)
          : [String(regionCode)];
      },
    },
    watch: {
      projects() {
        this.restyle();
      },
      selectedCountryId() {
        this.checkAndSwitchMap();
      },
      maxRegionProjects(newValue) {
        this.$emit('maxRegionProjectsChanged', newValue);
      },
    },
  };
</script>

<style scoped lang="scss">
  .project-map :deep(.map-component) {
    height: 100%;
  }
</style>
